/**
 * VÉRAULT Portal — ClientPortalPreview
 *
 * "Step into the client's shoes for any trip."
 *
 * The advisor lands here from inside a Journey via a "Preview client view"
 * link. We mount the ACTUAL <ClientTripHome /> component — same JSX, same
 * styling, same axios calls — but install a transient request interceptor
 * that rewrites `/client-portal/...` calls to `/advisor/preview/...` so the
 * advisor can read the client view of ANY trip (without impersonation
 * security risk).
 *
 * A sticky top banner makes it unmistakeable that this is a preview, with
 * a single "Exit to Journey" affordance back to the advisor surface.
 */
import React from "react";
import { useNavigate, useParams, useSearchParams, Outlet, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Eye, LogOut, X } from "lucide-react";
import { api } from "../../index";
import ClientTripHome from "../client/TripHome";
import ClientItinerary from "../client/Itinerary";
import ExperienceItinerary from "../client/experience/Itinerary";
import ClientJourney from "../client/Journey";
import ClientPayments from "../client/Payments";
import ClientDocuments from "../client/Documents";
import ClientTravelers from "../client/Travelers";
import ClientReadiness from "../client/Readiness";
import ClientConcierge from "../client/Concierge";
import ClientProposal from "../client/experience/Proposal";
import { PreviewProvider } from "../../contexts/PreviewContext";

export default function ClientPortalPreview() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [ready, setReady] = React.useState(false);
  const [clientName, setClientName] = React.useState("");

  React.useEffect(() => {
    if (!tripId) return;
    api.get(`/advisor/preview/trips/${tripId}`)
      .then(({ data }) => setClientName(data?.client_name || ""))
      .catch(() => {});
  }, [tripId]);

  // Wave 11q — Deep-link from advisor tab to matching client preview tab.
  // The advisor's "Preview client view" button passes ?tab=<journey-tab>;
  // we navigate ONCE to the matching client surface so they land on the
  // same content. Bounces happen only when the user is at the preview root.
  React.useEffect(() => {
    const requestedTab = searchParams.get("tab");
    if (!requestedTab) return;
    // Already at a sub-path? Don't redirect a second time.
    const here = location.pathname.replace(/\/+$/, "");
    const previewBase = `/portal/advisor/journeys/${tripId}/client-preview`;
    if (here !== previewBase) return;
    const tabToPath = {
      // JourneyDetail TABS source of truth (id → client preview path):
      // overview / planning / itinerary / logistics / client-lounge / money-documents
      "overview": "",                  // root = TripHome
      "planning": "",                  // planning has no client equivalent → land on TripHome
      "itinerary": "/itinerary",
      "logistics": "/payments",        // logistics ≈ payments/documents on client side
      "client-lounge": "/concierge",
      "money-documents": "/documents",
    };
    const dest = tabToPath[requestedTab];
    if (dest === undefined) return;
    navigate(`${previewBase}${dest}`, { replace: true });
  }, [tripId, searchParams, location.pathname, navigate]);

  // useLayoutEffect ensures the interceptor is installed BEFORE the child
  // <ClientTripHome /> fires its own data-fetching effects. Without this the
  // first API call hits /client-portal/... and 401s (advisor session has no
  // client role). The `ready` flag gates the child render for an extra safety
  // belt.
  React.useLayoutEffect(() => {
    if (!tripId) {
      setReady(false);
      return undefined;
    }
    const id = api.interceptors.request.use((config) => {
      const u = config.url || "";
      const cpTrip = `/client-portal/trips/${tripId}`;
      if (u.startsWith(cpTrip)) {
        let next = u.replace(cpTrip, `/advisor/preview/trips/${tripId}`);
        // Wave 26.8 — for the itinerary specifically, force `as_client=true`
        // so reveal gating + visibility filtering match the live client view.
        if (next.includes("/itinerary")) {
          next += (next.includes("?") ? "&" : "?") + "as_client=true";
        }
        config.url = next;
        return config;
      }
      // Wave 11i · Phase 3 — Broaden interception to ALL client-portal-
      // scoped GET endpoints used by Payments / Documents / Travelers /
      // Readiness / Concierge. Each becomes a preview-scoped read so the
      // advisor never collides with their own session's client data.
      const clientPortalScopedPaths = [
        "/client-portal/agreements",
        "/client-portal/forms",
        "/client-portal/payments",
        "/client-portal/documents",
        "/client-portal/travelers",
        "/client-portal/readiness",
        "/client-portal/concierge",
        "/client-portal/notifications",
        "/client-portal/inbox",
      ];
      for (const p of clientPortalScopedPaths) {
        if (u === p || u.startsWith(p + "?") || u.startsWith(p + "/")) {
          config.url = u.replace("/client-portal/", `/advisor/preview/trips/${tripId}/`);
          return config;
        }
      }
      return config;
    });
    setReady(true);
    return () => {
      api.interceptors.request.eject(id);
      setReady(false);
    };
  }, [tripId]);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="client-portal-preview">
      {/* Sticky banner — unmistakeable. Same dark surface as the website CMS
          preview banner so the pattern feels consistent across the platform. */}
      <div className="sticky top-0 z-50 bg-[#1A1A1A] text-white py-2.5 px-4 sm:px-6 flex items-center justify-between gap-3" data-testid="advisor-preview-banner">
        <div className="flex items-center gap-3 min-w-0">
          <Eye className="size-3.5 shrink-0 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <p className="font-mono text-[10px] uppercase tracking-[0.22em]" data-testid="ghost-mode-label">
            Ghost Mode{clientName ? ` · Previewing as ${clientName}` : ""}
          </p>
          <p className="font-accent italic text-[12.5px] text-white/70 truncate hidden sm:block">
            Read-only. No client actions, notifications, or data changes happen here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/portal/advisor/journeys/${tripId}`)}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/85 hover:text-white transition-colors"
          data-testid="advisor-preview-exit"
          aria-label="Exit preview"
        >
          <LogOut className="size-3" strokeWidth={1.5} />
          Exit to Journey
        </button>
      </div>

      {/* The actual client TripHome / Itinerary / etc., rendered against
          advisor-preview data + wrapped in PreviewProvider so child links
          stay within the preview wrapper instead of routing into real
          client-auth-protected routes. */}
      <div className="px-5 sm:px-8 lg:px-12 py-10 max-w-6xl mx-auto">
        {ready ? (
          <PreviewProvider
            isPreview
            base={`/portal/advisor/journeys/${tripId}/client-preview`}
          >
            <Routes>
              {/* Wave 11i · Phase 3 — Full Member-Suite parity in preview.
                  Mount every surface the live client sees so the advisor
                  experiences EXACTLY what the traveler experiences: trip
                  home, day itinerary, journey timeline, payments, docs,
                  travelers, readiness, concierge thread. All read-only
                  in preview — the interceptor + PreviewProvider keep
                  links + API calls scoped to the preview wrapper. */}
              <Route index element={<ClientTripHome />} />
              <Route path="proposal" element={<ClientProposal tripId={tripId} />} />
              <Route path="trip/:innerTripId/itinerary" element={<ClientItinerary />} />
              <Route path="itinerary" element={<ExperienceItinerary />} />
              <Route path="journey" element={<ClientJourney />} />
              <Route path="payments" element={<ClientPayments />} />
              <Route path="documents" element={<ClientDocuments />} />
              <Route path="travelers" element={<ClientTravelers />} />
              <Route path="readiness" element={<ClientReadiness />} />
              <Route path="concierge" element={<ClientConcierge />} />
            </Routes>
          </PreviewProvider>
        ) : (
          <div className="py-20 text-center">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Preparing client view…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
